import { useState } from "react";
import { motion } from "motion/react";

import type { FAQItem } from "@/landing/lib/types";
import { useEarlyAccess } from "@/features/early-access/EarlyAccessDialog";
import { CLI_FAQ_ITEMS } from "@/landing/components/cli-faq-data";
import { FaqItem } from "@/landing/components/FaqItem";
import { SectionEyebrow } from "@/landing/ui/SectionEyebrow";

export function CliFaq({ items = CLI_FAQ_ITEMS }: { items?: FAQItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Priority-access captures live inside some FAQ answers. Each source gets
  // its own dialog instance; an item's `priorityAccessSource` picks which
  // opener the FaqItem CTA fires.
  const mcp = useEarlyAccess("faq", "feature_MCP");
  const automations = useEarlyAccess("faq", "readyMadeAutomations");
  const openBySource = {
    feature_MCP: mcp.openEarlyAccess,
    readyMadeAutomations: automations.openEarlyAccess,
  } as const;

  return (
    <section id="faq" className="px-4 py-14 sm:px-6 md:py-20">
      <motion.div
        className="mx-auto max-w-3xl"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className="mb-12 text-center">
          <SectionEyebrow className="justify-center">FAQ</SectionEyebrow>
          <h2
            className="mt-4 text-3xl font-semibold tracking-[-0.03em] text-balance sm:text-4xl lg:text-5xl"
            style={{ color: "var(--cli-text)" }}
          >
            Frequently asked questions
          </h2>
          <p
            className="mt-3 text-base"
            style={{ color: "var(--cli-text-muted)" }}
          >
            Everything you need to know before getting started.
          </p>
        </div>

        <div className="overflow-hidden rounded-2xl border border-[var(--cli-border)] bg-[var(--ground)]">
          {items.map((item, index) => (
            <FaqItem
              key={index}
              item={item}
              isOpen={openIndex === index}
              isFirst={index === 0}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
              onPriorityAccess={
                item.priorityAccessSource === "feature_MCP" ||
                item.priorityAccessSource === "readyMadeAutomations"
                  ? openBySource[item.priorityAccessSource]
                  : undefined
              }
            />
          ))}
        </div>
      </motion.div>
      {mcp.dialog}
      {automations.dialog}
    </section>
  );
}
